<template>
  <div class="md:hidden">
    <div v-if="getMobileMenuOpen" class="fixed inset-0 z-40 flex">
      <!--
        Off-canvas menu overlay, show/hide based on off-canvas menu state.

        Entering: "transition-opacity ease-linear duration-300"
          From: "opacity-0"
          To: "opacity-100"
        Leaving: "transition-opacity ease-linear duration-300"
          From: "opacity-100"
          To: "opacity-0"
      -->
      <Transition
        appear
        enter-active-class="transition-opacity ease-linear duration-300"
        enter-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition-opacity ease-linear duration-300"
        leave-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div class="fixed inset-0">
          <div class="absolute inset-0 bg-secondary-600 opacity-75" />
        </div>
      </Transition>
      <Transition
        appear
        enter-active-class="transition ease-in-out duration-300 transform"
        enter-class="-translate-x-full"
        enter-to-class="translate-x-0"
        leave-active-class="transition ease-in-out duration-300 transform"
        leave-class="translate-x-0"
        leave-to-class="-translate-x-full"
      >
        <div
          v-if="getMobileMenuOpen"
          class="relative flex w-full max-w-xs flex-1 flex-col bg-default"
        >
          <div class="absolute top-0 right-0 -mr-14 p-1">
            <button
              class="flex h-12 w-12 items-center justify-center rounded-full focus:bg-secondary-600 focus:outline-none"
              aria-label="Close sidebar"
              @click="closeMenu"
            >
              <XMarkIcon class="h-6 w-6 text-default" aria-hidden="true" />
            </button>
          </div>
          <div
            class="flex items-center mx-auto mt-4"
            :class="[getTypeFromImage === 'svg' ? '' : 'h-[32px] w-[240px]']"
          >
            <img
              :src="logo"
              :alt="clientId"
              :class="[
                getTypeFromImage !== 'svg' ? 'object-contain' : 'h-8 w-auto',
              ]"
              @error="imageLoadOnError"
              data-testid="mobile-app-logo"
            />
          </div>
          <h1 v-if="brokenImage" class="text-center font-semibold text-4xl">
            {{ clientId }}
          </h1>
          <div class="flex flex-col px-4 pt-8">
            <span class="inline-flex rounded-md shadow-sm">
              <RouterLink
                to="/message-compose"
                class="inline-flex w-full items-center justify-center rounded-md border border-transparent bg-primary-600 px-4 py-2 text-sm font-medium leading-5 text-default transition duration-150 ease-in-out hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                New Message
              </RouterLink>
            </span>
          </div>
          <div
            v-if="getMenuItemsHasError"
            class="px-4 py-4 font-medium uppercase tracking-wider text-secondary-500"
          >
            Error in fetching data. Please try again
          </div>

          <div
            class="mt-5 h-0 flex-1 overflow-y-auto"
            v-else-if="!getMenuItemsHasError && getMenuItems.length > 0"
          >
            <nav class="space-y-1 px-2">
              <MobileMenuItem
                v-for="menu in getMenuItems"
                :service="menu.service"
                :label="menu.label"
                :key="menu.id"
                :unreadMessageCount="unreadMessageCount"
              />
            </nav>
          </div>
          <div
            v-else
            class="px-4 py-4 font-medium uppercase tracking-wider text-secondary-500"
          >
            No Data
          </div>
          <slot name="bottom" />
          <ProfileAvatar />
        </div>
      </Transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import MobileMenuItem from "@/components/menu/mobile-menu/mobile-menu-item.vue";
import { computed, ref } from "vue";
import ProfileAvatar from "@/components/profile-avatar.vue";
import { useMenuStore } from "@/pinia/useMenuStore";
import { storeToRefs } from "pinia";
import { useAuthStore } from "@/pinia/useAuthStore";

defineProps({
  unreadMessageCount: {
    type: Number,
    default: 0,
  },
});
const { getMenuItems, getMenuItemsHasError, getMobileMenuOpen } =
  storeToRefs(useMenuStore());
const { setMobileMenuDisplay } = useMenuStore();
const { getUserProfile, getLogoURL } = storeToRefs(useAuthStore());
const brokenImage = ref(false);
const clientId = getUserProfile.value?.decodedData?.brand ?? "CONNECT3";
const getTypeFromImage = computed(() => {
  if (getLogoURL.value) {
    const parts = getLogoURL.value.split(".");
    return parts[parts.length - 1];
  }
});

const imageLoadOnError = (e: any) => {
  e.target.style.display = "none";
  brokenImage.value = true;
};

const logo = computed(() => {
  if (getLogoURL.value) {
    return new URL(getLogoURL.value, window.location.origin).href;
  }
});

const closeMenu = () => {
  setMobileMenuDisplay(false);
};
</script>
